import { Component, OnDestroy, ViewChild } from '@angular/core';
import { CustomInputComponent } from 'components/input/custom-input/custom-input.component';
import { CustomSelectComponent } from 'components/input/custom-select/custom-select.component';
import { SubmitButtonComponent } from 'components/input/submit-button/submit-button.component';
import { SoftPopupComponent } from 'components/layout/soft-popup/soft-popup.component';
import { CredentialType } from 'enums/CredentialType';
import { InputType } from 'enums/InputType';
import { SelectListItemModel } from 'models/SelectList';
import { Layout } from 'modules/layout';
import { ListView } from 'modules/list-view';
import { TranslatePipe } from 'pipes/translate.pipe';
import { Subscription } from 'rxjs';
import { CredentialRequired } from 'services/credential.service';
import { FormValidationService } from 'services/form-validation.service';
import { ComponentUrl, NavigationService } from 'services/navigation.service';
import { NotificationService } from 'services/notification.service';
import { PushService } from 'services/push.service';
import { CopyDataDto, CopyDataProperty, SyncService } from 'services/sync.service';
import { UserService } from 'services/user.service';

@Component({
	selector: 'sync-data',
	templateUrl: './sync-data.component.html',
	providers: [FormValidationService],
	imports: [Layout, CustomInputComponent, CustomSelectComponent, SoftPopupComponent, ListView, TranslatePipe]
})
@ComponentUrl('sync-data')
@CredentialRequired(CredentialType.SyncEnvironmentData)
export class SyncDataComponent implements OnDestroy {

	@ViewChild('copyButton') copyButton!: SubmitButtonComponent;

	private copyProcess?: Subscription;
	private connectionString = '';

	inputTypeEnum = InputType;
	public useManualSource = false;
	public manualSource = 'Server=tcp:test-godb1-sqlserver-go.database.windows.net;Database=test-accounting-db-go;User Id=testtam;Password=UOVu%2d98FMDE8z6JaFf;Trust Server Certificate=True';
	public selectedEnvironment?: string;
	public selectedDatabase?: string;
	public sourceDatabase?: Table[];
	public targetDatabase?: Table[];
	public copyHistoricalData = false;
	public loading = false;
	public matchedProperties: CopyDataProperty[] = [];
	public targetDatabaseList?: SelectListItemModel[];

	public showPropertySelector = false;
	public propertySelectorOffsetTop = 0;
	public filteredTargetProperties: CopyDataProperty[] = [];
	public currentPropertySelectorTable?: Table;
	public currentPropertySelectorColumn?: string;

	public environmentsList = [
		new SelectListItemModel('dev', 'Development'),
		new SelectListItemModel('test', 'Test'),
		new SelectListItemModel('preprod', 'PreProd'),
		new SelectListItemModel('prod', 'Production')
	];
	public databasesList = [
		new SelectListItemModel('accounting', 'Accounting'),
		new SelectListItemModel('communication', 'Communication'),
		new SelectListItemModel('distribution', 'Distribution'),
		new SelectListItemModel('gormcrm', 'GormCrm'),
		new SelectListItemModel('mailprocessing', 'Mailprocessing'),
		new SelectListItemModel('identity', 'Identity'),
		new SelectListItemModel('language', 'Language'),
		new SelectListItemModel('sslcertificates', 'SslCertificates')
	];


	constructor(private readonly syncService: SyncService, private readonly userService: UserService, private readonly pushService: PushService, private readonly navigation: NavigationService, private readonly notificationService: NotificationService) {
		
	}

	getDatabaseOverview() {
		if (!this.selectedDatabase || (!this.useManualSource && !this.selectedEnvironment) || (this.useManualSource && !this.manualSource)) return;

		this.connectionString = `Server=tcp:${this.selectedEnvironment}-godb1-sqlserver-go.database.windows.net;Authentication=Active Directory Default; Database=${this.selectedEnvironment}-${this.selectedDatabase}-db-go;`;
		if (this.useManualSource) {
			this.connectionString = this.manualSource;
		}

		this.loading = true;
		let promises = [];
		promises.push(this.syncService.getDatabaseOverviewByConnectionString(this.connectionString).then(tablesAndColumns => {
			this.sourceDatabase = Object.entries(tablesAndColumns)
				.filter(([tablename, columns]) => Array.isArray(columns))
				.map(([tablename, columns]) => new Table(tablename, columns));
		}));
		promises.push(this.syncService.getDatabaseOverview(this.selectedDatabase).then(tablesAndColumns => {
			this.targetDatabase = Object.entries(tablesAndColumns)
				.filter(([tablename, columns]) => Array.isArray(columns))
				.map(([tablename, columns]) => new Table(tablename, columns));
			this.targetDatabaseList = this.targetDatabase.flatMap(td => td.columns.map(column => new SelectListItemModel(`${td.tablename}|||${column}`, `${td.tablename} - ${column}`)));
		}));
		Promise.all(promises).then(() => {
			this.loading = false;
			this.sourceDatabase?.forEach(table => {
				let matchingTargetTable = this.targetDatabase?.find(t => t.tablename == table.tablename);
				if (!matchingTargetTable)
					return;

				matchingTargetTable.columns.filter(c => table.columns.indexOf(c) >= 0).forEach(column => {
					let copyDataProperty = new CopyDataProperty();
					copyDataProperty.sourceTable = table.tablename;
					copyDataProperty.sourceProperty = column;
					copyDataProperty.targetTable = matchingTargetTable!.tablename;
					copyDataProperty.targetProperty = column;
					this.matchedProperties.push(copyDataProperty);
				});

			});
		});
	}

	getStatus(sourceTable: Table): string {
		let matchedProperties = this.matchedProperties.filter(mp => mp.sourceTable == sourceTable.tablename).map(mp => mp.sourceProperty);
		if (sourceTable.columns.every(c => matchedProperties.indexOf(c) >= 0))
			return 'all';
		if (sourceTable.columns.some(c => matchedProperties.indexOf(c) >= 0))
			return 'some';
		return 'none';
	}

	setMatchedProperty(table: string, column: string) {
		if (this.currentPropertySelectorTable == undefined || this.currentPropertySelectorColumn == undefined) return;
		let matchedProperty = new CopyDataProperty();
		matchedProperty.sourceTable = this.currentPropertySelectorTable.tablename;
		matchedProperty.sourceProperty = this.currentPropertySelectorColumn;
		matchedProperty.targetTable = table;
		matchedProperty.targetProperty = column;

		let indexOfPreviouslyMatchedProperty = this.matchedProperties.findIndex(mp => mp.targetTable == matchedProperty.targetTable && mp.targetProperty == matchedProperty.targetProperty);
		if (indexOfPreviouslyMatchedProperty >= 0) {
			this.matchedProperties.splice(indexOfPreviouslyMatchedProperty, 1);
		}
		this.matchedProperties.push(matchedProperty);

		this.currentPropertySelectorTable = undefined;
		this.currentPropertySelectorColumn = undefined;
		this.showPropertySelector = false;
	}

	getMatch(table: Table, column: string): CopyDataProperty | undefined {
		return this.matchedProperties.find(mp => mp.sourceTable == table.tablename && mp.sourceProperty == column);
	}

	openPropertySelector(table: Table, column: string, event: MouseEvent) {
		this.currentPropertySelectorTable = table;
		this.currentPropertySelectorColumn = column;

		let target = event?.target as HTMLElement, targetParent = target.offsetParent! as HTMLElement;
		this.propertySelectorOffsetTop = 35 + target.offsetTop + targetParent.offsetTop;

		let foundMatchIndex = this.matchedProperties.findIndex(mp => mp.sourceTable == table.tablename && mp.sourceProperty == column);
		if (foundMatchIndex >= 0)
			this.matchedProperties.splice(foundMatchIndex, 1);

		this.filteredTargetProperties = this.targetDatabase!.flatMap(td => td.columns.map(column => new CopyDataProperty(td.tablename, column))).filter(property => !this.matchedProperties.some(mp => mp.targetTable == property.targetTable && mp.targetProperty == property.targetProperty));
		this.showPropertySelector = true;
	}

	clearTable(table: Table) {
		this.matchedProperties.filter(mp => mp.sourceTable == table.tablename).forEach(mp => {
			this.matchedProperties.splice(this.matchedProperties.findIndex(property => property.sourceTable == mp.sourceTable && property.sourceProperty == mp.sourceProperty), 1);
		});
	}

	clearAndCopyData() {

		if (this.copyProcess) this.copyProcess.unsubscribe();
		this.copyProcess = this.pushService.onProgressChanges(this.userService.getCurrentUser().id!).subscribe(event => {
			this.copyButton.progression = { processed: event.processed!, total: event.total! };
		});

		let copyDataDto = new CopyDataDto();
		copyDataDto.connectionString = this.connectionString;
		copyDataDto.targetDatabase = this.selectedDatabase!;
		copyDataDto.copyHistoricalData = this.copyHistoricalData;
		copyDataDto.properties = this.matchedProperties;
		this.syncService.clearAndCopyData(copyDataDto).then(() => {
			this.notificationService.success('sync-data.copy-data-complete');
			this.navigation.refreshView(SyncDataComponent);
		});
	}

	ngOnDestroy(): void {
		if (this.copyProcess) this.copyProcess.unsubscribe();
	}

}

export class Table {
	public tablename: string;
	public columns: string[];
	public matchedProperty?: string;
	constructor(tablename: string, columns: string[]) {
		this.tablename = tablename;
		this.columns = columns;
	}
}
